/**
 * ATTEST-UI-1 — typed client for the seven Session-1 attest endpoints.
 *
 * Two things make this file different from the other API modules:
 *
 * 1. **A refusal is a payload, not a failure.** `core/attest` raises
 *    `AttestError`, the router turns it into `HTTPException(detail={...})`, and
 *    that dict names the guardrail that fired, the offending sentence and —
 *    where the policy has one — the safe alternative. `httpErrors.fromAxiosError`
 *    cannot see any of it: it reads `data.error.{code,message}` or a *string*
 *    `data.detail`, so an attest refusal would degrade to "422 -
 *    Unprocessable Entity". `parseAttestRefusal` reads the dict directly, which
 *    is why every screen here calls it instead of `formatApiError`.
 * 2. **Nothing is computed locally.** Which assertions a type permits, which it
 *    denies, and whether a letter may be issued at all are the server's answers.
 *    The UI renders them; it never second-guesses or pre-empts them.
 *
 * The wire shapes themselves live in `attestTypes.ts` and are re-exported below,
 * so `@/components/attest/api` remains the single import for every attest screen.
 */
import { apiClient } from '@/api/client'
import type {
  AttestPolicy,
  AttestRefusal,
  ClassifyRequest,
  EvidenceIntakeResponse,
  LetterArtifact,
  LetterRequestBody,
  LetterTypeSummary,
  RoutingDecision,
} from '@/components/attest/apiTypes'

// The wire shapes live in `attestTypes.ts` and the gate vocabulary in
// `attestGateTypes.ts` (FILE_SIZE_POLICY); both are re-exported here so screens
// import everything attest from one module.
export * from '@/components/attest/apiTypes'
export * from '@/components/attest/apiGateTypes'

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

const ATTEST_ERROR_CODES = new Set([
  'ATTEST_REFUSED',
  'ATTEST_POLICY_INVALID',
  'ATTEST_ASSERTION_DENIED',
  'ATTEST_ASSURANCE_EXCEEDED',
  'ATTEST_ENGAGEMENT_REFUSED',
  'ATTEST_INDEPENDENCE_IMPAIRED',
  'ATTEST_ISSUER_NOT_ELIGIBLE',
  'ATTEST_EVIDENCE_MISSING',
  'ATTEST_EVIDENCE_NOT_VERIFIED',
  'ATTEST_LETTER_TYPE_UNKNOWN',
  'ATTEST_INPUT_MISSING',
  'ATTEST_FIRM_PROFILE_MISSING',
])

/**
 * Pull a structured refusal out of an axios error, or return null when the
 * failure was something else (network, 500, auth) that the generic error path
 * should handle. Never throws: a screen that cannot parse the refusal still has
 * to show the user something, and `formatApiError` is the fallback.
 */
export function parseAttestRefusal(err: unknown): AttestRefusal | null {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response
  if (!response) return null
  const data = response.data
  if (!data || typeof data !== 'object') return null
  const detail = (data as { detail?: unknown }).detail
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null
  const record = detail as Record<string, unknown>
  const code = typeof record.error_code === 'string' ? record.error_code : ''
  if (!ATTEST_ERROR_CODES.has(code)) return null

  const { error_code: _code, message, user_message, ...rest } = record
  return {
    status: response.status ?? 0,
    error_code: code,
    message: typeof message === 'string' ? message : '',
    user_message: typeof user_message === 'string' ? user_message : '',
    detail: rest,
  }
}

/**
 * `/classify` refuses with **200 + `refused: true`** — the caller asked for a
 * classification and "nothing permissible" is one. That is right for the API and
 * wrong for the UI, where a refusal must look the same however it arrived, so
 * the decision is reshaped into the refusal the panel already knows how to
 * render. The per-item `hits` carry the rationale and safe alternative, which is
 * exactly what makes the wizard a redirection rather than a dead end.
 */
export function routingRefusal(decision: RoutingDecision): AttestRefusal | null {
  if (!decision.refused) return null
  const hits = decision.excluded_items.flatMap(item => item.hits ?? [])
  return {
    status: 200,
    error_code: 'ATTEST_ENGAGEMENT_REFUSED',
    message: decision.refusal_detail,
    // Reason-aware, because the default sentence is a lie for one of the
    // reasons: INTENT_CHECK_UNAVAILABLE means the screen never ran, so "as
    // written" points the CPA at wording that was never the problem and sends
    // them off to rewrite text that would pass on a retry.
    user_message: decision.refusal_reason === 'INTENT_CHECK_UNAVAILABLE'
      ? 'The compliance check could not run, so nothing could be cleared. '
        + 'This is not a finding against your wording — the items are unverified, '
        + 'not prohibited. Try again, or have a CPA review them.'
      : 'This request cannot be issued as a compliant letter as written.',
    detail: {
      refusal_reason: decision.refusal_reason,
      denied_hits: hits,
      policy_version: decision.policy_version,
    },
  }
}

/**
 * A `.docx` refusal arrives as a Blob because the request asked for one, so the
 * JSON has to be read back off the blob before `parseAttestRefusal` can see it.
 * Mutates nothing: it returns a shallow copy of the error with `data` replaced.
 */
export async function hydrateBlobError(err: unknown): Promise<unknown> {
  const response = (err as { response?: { data?: unknown } })?.response
  const blob = response?.data
  if (!(blob instanceof Blob)) return err
  try {
    response!.data = JSON.parse(await blob.text())
  } catch {
    /* not JSON — leave the original error for the generic handler */
  }
  return err
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export async function fetchLetterTypes(): Promise<LetterTypeSummary[]> {
  const { data } = await apiClient.get<{ letter_types: LetterTypeSummary[] }>(
    '/v1/attest/letter-types',
  )
  return data.letter_types ?? []
}

export async function fetchLetterType(typeId: string): Promise<LetterTypeSummary> {
  const { data } = await apiClient.get<LetterTypeSummary>(
    `/v1/attest/letter-types/${encodeURIComponent(typeId)}`,
  )
  return data
}

export async function fetchPolicy(): Promise<AttestPolicy> {
  const { data } = await apiClient.get<AttestPolicy>('/v1/attest/policy')
  return data
}

export async function classifyRequest(body: ClassifyRequest): Promise<RoutingDecision> {
  const { data } = await apiClient.post<RoutingDecision>('/v1/attest/classify', body)
  return data
}

export async function previewLetter(body: LetterRequestBody): Promise<LetterArtifact> {
  const { data } = await apiClient.post<LetterArtifact>('/v1/attest/letters/preview', body)
  return data
}

/**
 * Attach a real document as evidence: store it, classify it, read it.
 *
 * The response is a *proposal* — a blob id for `Evidence.location`, a suggested
 * citation, and any figures the core parser recognised. None of it is applied
 * to the letter here. The CPA picks the figure that belongs in their sentence,
 * and the letter still runs every gate afterwards.
 */
export async function uploadEvidence(file: File): Promise<EvidenceIntakeResponse> {
  const body = new FormData()
  body.append('file', file)
  // Explicit, like every other upload path here: the client's default
  // `application/json` would otherwise ride along and the form would not parse.
  const { data } = await apiClient.post<EvidenceIntakeResponse>(
    '/v1/attest/letters/evidence', body,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data
}

/** Streams the gated `.docx`. Same gates as preview — it can still refuse. */
export async function downloadLetterDocx(
  body: LetterRequestBody,
  filename: string,
): Promise<void> {
  const res = await apiClient.post('/v1/attest/letters/docx', body, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
